<script>
import ContentBox from '@/components/ui/ContentBox';
import ImageInput from '@/components/form-controls/ImageInput';
import LanguageSelect from '@/components/i18n/LanguageSelect';
import PermissionCard from '@/components/users/PermissionCard';
import RequirePlan from '@/components/ui/RequirePlan';
import animate from '@/modules/animate-css';
import uuid from 'uuid/v4';
import '@shoelace-style/shoelace/dist/components/qr-code/qr-code';

export default {
  name: 'UserForm',
  components: {
    ContentBox,
    ImageInput,
    LanguageSelect,
    PermissionCard,
    RequirePlan
  },
  props: {
    data: Object,
    deleting: Boolean,
    disabled: Boolean,
    invalid: Array,
    initialTab: String,
    isNew: Boolean,
    isProfile: Boolean,
    saving: Boolean
  },
  data() {
    return {
      activeTab: 'general',
      apiToken: null,
      cleanState: {},
      form: {
        name: '',
        email: '',
        avatar: null,
        bio: '',
        isAdmin: false,
        canManageBilling: false,
        canManageSettings: false,
        canManageSites: false,
        canManageUsers: false,
        permissions: [],
        currentPassword: '',
        password: ''
      },
      isApiUpdating: false,
      isTfaEnabled: this.$store.state.user.requiresTfa,
      isDisablingTfa: false,
      permissionSearch: '',
      showCurrentPassword: false,
      showPassword: false,
      showApiToken: false,
      sites: []
    };
  },
  computed: {
    // Hide credential management (change password and two-factor auth) for non-admin/non-owner users
    // when custom logins are enabled. These users authenticate through an external system.
    shouldHideSecuritySettings() {
      const user = this.$store.state.user;
      return user.account.customLoginsEnabled && !user.isAdmin && !user.isOwner;
    }
  },
  created() {
    // Set the initial data
    if (this.data) {
      this.setData(this.data);
    }

    // Set initial active tab
    if (this.initialTab) {
      this.activeTab = this.initialTab;
    }

    // Fetch the API token (only when admins are editing their profile)
    if (this.isProfile && this.$store.state.user.isAdmin) {
      this.$http
        .get(`/users/${this.$store.state.user.id}/api-token`)
        .then(res => (this.apiToken = res.data.apiToken))
        .catch(() => (this.apiToken = null));
    }

    // Set the initial clean state
    this.resetCleanState();
  },
  methods: {
    // Gets a list of sites for autocomplete
    getSites(search) {
      this.$http
        .get('/sites', {
          params: {
            search,
            limit: 20,
            sortBy: 'name'
          }
        })
        .then(res => (this.sites = res.data.sites))
        .catch(() => (this.sites = []));
    },

    // Look for a gravatar when the email changes
    handleEmailChange() {
      // If an avatar isn't set OR if it's set to a gravatar, update it
      if (this.form.email.length && (!this.form.avatar || this.form.avatar.indexOf('gravatar.com') > -1)) {
        this.$http
          .get('/utilities/gravatar', {
            params: {
              email: this.form.email,
              size: 512,
              verify: true
            }
          })
          .then(res => {
            if (res.data.url) {
              this.form.avatar = res.data.url;
            }
          })
          .catch(() => {
            /* do nothing */
          });
      }
    },

    // Called when the isAdmin control is changed
    handleIsAdminChange() {
      if (!this.isAdmin) {
        this.form.canManageBilling = false;
        this.form.canManageSettings = false;
        this.form.canManageSites = false;
        this.form.canManageUsers = false;
      }
    },

    // Adds a permission
    addPermission(site) {
      const permission = {
        id: uuid(),
        userId: this.$route.params.userId,
        siteId: site.id,
        site,
        pages: null
      };
      const duplicate = this.form.permissions.filter(p => p.siteId === permission.siteId);

      // Prepend the permission
      if (duplicate.length) {
        const el = this.$el.querySelector(`.permission-card[data-id="${duplicate[0].id}"]`);
        animate(el, 'bounce', 1);
      } else {
        this.form.permissions.unshift(permission);
      }

      // Reset the control
      this.permissionSearch = '';
    },

    // Updates a permission
    updatePermission(permissionId, pages) {
      this.form.permissions = this.form.permissions.map(p => {
        if (p.id === permissionId) {
          p.pages = Array.isArray(pages) ? pages : null;
        }

        return p;
      });
    },

    // Revokes a permission
    revokePermission(permission) {
      this.form.permissions = this.form.permissions.filter(p => p.id !== permission.id);
    },

    // Returns the form's data
    getData() {
      return Object.assign({}, this.form);
    },

    // Sets the form's data
    setData(data) {
      Object.keys(this.form).forEach(key => {
        this.form[key] = typeof data[key] === 'undefined' ? this.form[key] : data[key];
      });
    },

    // Tells if the current state is different than the clean state
    isDirty() {
      return JSON.stringify(this.cleanState) !== JSON.stringify(this.form);
    },

    // Sets the clean state to the current state
    resetCleanState() {
      this.cleanState = Object.assign({}, this.form);
    },

    enableTfa() {
      // Redirect to the setup page
      this.$router.push({ name: 'EnableTfa' });
    },

    disableTfa() {
      // Disable two-factor auth
      this.$prompt(this.$t('Please enter your password to continue.'), {
        title: this.$t('Disable Two-factor Authentication'),
        confirmButtonText: this.$t('Disable'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel'),
        inputType: 'password'
      })
        .then(result => {
          const password = result.value;
          this.isDisablingTfa = true;
          this.$http
            .delete(`/users/${this.$store.state.user.id}/tfa`, {
              data: { password }
            })
            .then(() => {
              // Update user
              this.$store.commit('setUser', Object.assign({}, this.$store.state.user, { requiresTfa: false }));
              this.$store.commit('setTfa', {
                isRequiredByAccount: this.$store.state.user.account.requiresTfa,
                isRequired: false,
                isVerified: false
              });
              this.isTfaEnabled = false;
            })
            .catch(err => {
              this.$notify({
                type: 'error',
                title: this.$t('Error'),
                message: err.response.data.message
              });
            })
            .finally(() => (this.isDisablingTfa = false));
        })
        .catch(() => {
          // Do nothing
        });
    },

    enableApi() {
      this.isApiUpdating = true;
      this.$http
        .post(`/users/${this.$store.state.user.id}/api-token`)
        .then(res => {
          this.apiToken = res.data.apiToken;
          this.showApiToken = true;
        })
        .finally(() => (this.isApiUpdating = false));
    },

    disableApi() {
      this.$confirm(
        this.$t(
          'This will destroy your token and disable API access. All applications that use this token will stop working.'
        ),
        {
          title: this.$t('Disable'),
          confirmButtonText: this.$t('Disable'),
          confirmButtonClass: 'el-button--danger',
          cancelButtonText: this.$t('Cancel'),
          callback: action => {
            if (action === 'confirm') {
              this.isApiUpdating = true;
              this.$http
                .delete(`/users/${this.$store.state.user.id}/api-token`)
                .then(() => (this.apiToken = null))
                .finally(() => (this.isApiUpdating = false));
            }
          }
        }
      );
    },

    regenerateApiToken() {
      this.$confirm(
        'This will destroy your old token and create a new one. All applications that use the old token will need to be updated if you do this.',
        {
          title: this.$t('Regenerate'),
          confirmButtonText: this.$t('Regenerate'),
          confirmButtonClass: 'el-button--danger',
          cancelButtonText: this.$t('Cancel'),
          callback: action => {
            if (action === 'confirm') {
              this.isApiUpdating = true;
              this.$http
                .delete(`/users/${this.$store.state.user.id}/api-token`)
                .then(() => this.$http.post(`/users/${this.$store.state.user.id}/api-token`))
                .then(res => {
                  this.apiToken = res.data.apiToken;
                  this.showApiToken = true;
                })
                .finally(() => (this.isApiUpdating = false));
            }
          }
        }
      );
    }
  }
};
</script>

<template>
  <el-form
    ref="userForm"
    :disabled="disabled"
    class="user-form"
    label-position="top"
    @submit.native.prevent="$emit('submit')"
  >
    <content-box>
      <el-tabs v-model="activeTab">
        <!-- General -->
        <el-tab-pane :label="$t('General')" name="general">
          <fieldset>
            <!-- Avatar -->
            <el-form-item class="text-center">
              <image-input
                v-model="form.avatar"
                :value="form.avatar"
                :default-image="require('@/assets/user-light.svg')"
                :title="$t('Change Profile Picture')"
                :height="128"
                class="avatar-input"
                mime-type="image/jpeg"
                crop
              />
            </el-form-item>

            <!-- Name -->
            <el-form-item :label="$t('Name')" :class="{ 'is-error': invalid.includes('name') }">
              <el-input
                ref="name"
                v-model="form.name"
                :class="{ 'is-error': invalid.includes('name') }"
                type="text"
                auto-complete="off"
              />
            </el-form-item>

            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="isProfile ? 12 : 24">
                <!-- Email -->
                <el-form-item :label="$t('Email')" :class="{ 'is-error': invalid.includes('email') }">
                  <el-input
                    v-model="form.email"
                    :class="{ 'is-error': invalid.includes('email') }"
                    type="email"
                    auto-complete="off"
                    @change="handleEmailChange"
                  />
                  <div v-if="isNew" class="text-form-description">
                    {{ $t('An invitation will be sent to this email address when the user is created.') }}
                  </div>
                </el-form-item>
              </el-col>
              <el-col v-if="isProfile" :xs="24" :sm="24" :md="isProfile ? 12 : 24">
                <!-- Language picker -->
                <el-form-item :label="$t('Language')">
                  <language-select />
                </el-form-item>
              </el-col>
            </el-row>

            <!-- Bio -->
            <el-form-item :label="$t('Bio')" :class="{ 'is-error': invalid.includes('bio') }">
              <el-input
                v-model="form.bio"
                :autosize="{ minRows: 4, maxRows: 4 }"
                :rows="4"
                type="textarea"
                max-rows="4"
                auto-complete="off"
              />
              <div class="text-form-description">
                <span v-if="isProfile">
                  {{ $t('Type a few sentences about yourself.') }}
                </span>
                <span v-else>
                  {{ $t('Type a few sentences about this user.') }}
                </span>
              </div>
            </el-form-item>
          </fieldset>
        </el-tab-pane>

        <!-- Permissions -->
        <el-tab-pane v-if="!isProfile" :label="$t('Permissions')" name="permissions">
          <el-alert
            v-if="form.isAdmin"
            :title="form.name"
            :description="
              $t(
                'This user is an administrator so they have access to all sites. You need to disable administrative privileges if you want to limit their access.'
              )
            "
            :closable="false"
            type="info"
          />
          <div v-else>
            <el-form-item :label="$t('This user will have access to the following sites.')">
              <el-select
                v-model="permissionSearch"
                :class="{ 'is-error': invalid.includes('url') }"
                :debounce="300"
                :remote-method="getSites"
                :loading-text="$t('Loading...')"
                :no-data-text="$t('No Results')"
                :placeholder="$t('Start typing to add a site...')"
                filterable
                remote
                @change="addPermission"
              >
                <el-option v-for="site in sites" :key="site.id" :label="site.name" :value="site" />
              </el-select>
            </el-form-item>

            <!-- Permission cards -->
            <div ref="permissionCards" class="permission-cards">
              <permission-card
                v-for="permission in form.permissions"
                :key="permission.id"
                :permission="permission"
                @revoke="revokePermission(permission)"
                @update="updatePermission(permission.id, $event)"
              />
            </div>
          </div>
        </el-tab-pane>

        <!-- Advanced -->
        <el-tab-pane v-if="!isProfile" :label="$t('Advanced')" name="advanced">
          <fieldset>
            <!-- Make admin -->
            <el-form-item>
              <el-switch
                v-model="form.isAdmin"
                :checked="form.isAdmin"
                :active-text="$t('Make Administrator')"
                auto-complete="off"
                @change="handleIsAdminChange"
              />
              <div class="text-form-description">
                {{ $t('Give this user administrative privileges.') }}
              </div>
            </el-form-item>

            <!-- Admin-only options -->
            <div v-show="form.isAdmin" class="admin-only-options">
              <!-- Manage settings -->
              <el-form-item>
                <el-switch
                  v-model="form.canManageSettings"
                  :checked="form.canManageSettings"
                  :active-text="$t('Manage Settings')"
                  :disabled="!form.isAdmin"
                  auto-complete="off"
                />
                <div class="text-form-description">
                  {{ $t('Allow this user to change the settings for this account.') }}
                </div>
              </el-form-item>

              <!-- Manage billing -->
              <el-form-item>
                <el-switch
                  v-model="form.canManageBilling"
                  :checked="form.canManageBilling"
                  :active-text="$t('Manage Billing')"
                  :disabled="!form.isAdmin"
                  auto-complete="off"
                />
                <div class="text-form-description">
                  {{ $t('Allow this user to view invoices and manage payment info.') }}
                </div>
              </el-form-item>

              <!-- Manage sites -->
              <el-form-item>
                <el-switch
                  v-model="form.canManageSites"
                  :checked="form.canManageSites"
                  :active-text="$t('Manage Sites')"
                  :disabled="!form.isAdmin"
                  auto-complete="off"
                />
                <div class="text-form-description">
                  {{ $t('Allow this user to create, delete, and update sites.') }}
                </div>
              </el-form-item>

              <!-- Manage users -->
              <el-form-item>
                <el-switch
                  v-model="form.canManageUsers"
                  :checked="form.canManageUsers"
                  :active-text="$t('Manage Users')"
                  :disabled="!form.isAdmin"
                  auto-complete="off"
                />
                <div class="text-form-description">
                  {{ $t('Allow this user to create, delete, and update users.') }}
                </div>
              </el-form-item>
            </div>
          </fieldset>

          <fieldset v-if="!isNew" class="divided">
            <!-- Delete -->
            <h2>{{ $t('Delete') }}</h2>
            <p>
              {{ $t('This will delete the user and revoke all access to the CMS.') }}
            </p>
            <el-form-item>
              <el-button :loading="deleting" type="danger" @click="$emit('delete')">
                {{ $t('Delete This User') }}
              </el-button>
            </el-form-item>
          </fieldset>
        </el-tab-pane>

        <!-- Change password -->
        <el-tab-pane v-if="isProfile && !shouldHideSecuritySettings" :label="$t('Change Password')" name="advanced">
          <!-- Current password -->
          <el-form-item :label="$t('Current Password')" :class="{ 'is-error': invalid.includes('currentPassword') }">
            <el-input
              v-model="form.currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              auto-complete="off"
            >
              <a slot="suffix" href="#" @click.prevent="showCurrentPassword = !showCurrentPassword">
                <i
                  :class="{
                    fal: true,
                    'el-icon-fa-eye': !showCurrentPassword,
                    'el-icon-fa-eye-slash': showCurrentPassword
                  }"
                />
              </a>
            </el-input>
          </el-form-item>

          <!-- New password -->
          <el-form-item :label="$t('New Password')" :class="{ 'is-error': invalid.includes('password') }">
            <el-input v-model="form.password" :type="showPassword ? 'text' : 'password'" auto-complete="off">
              <a slot="suffix" href="#" @click.prevent="showPassword = !showPassword">
                <i
                  :class="{
                    fal: true,
                    'el-icon-fa-eye': !showPassword,
                    'el-icon-fa-eye-slash': showPassword
                  }"
                />
              </a>
            </el-input>
            <div class="text-form-description">
              {{ $t('Passwords must be at least eight characters long.') }}
            </div>
          </el-form-item>
        </el-tab-pane>

        <!-- Authentication -->
        <el-tab-pane v-if="isProfile && !shouldHideSecuritySettings" :label="$t('Authentication')" name="authentication">
          <!-- Two-factor authentication -->
          <h2>{{ $t('Two-factor Authentication') }}</h2>
          <p>
            {{
              $t(
                'This adds an extra layer of security to your account by requiring access to a known device (such as a smartphone) before logging into your account.'
              )
            }}
          </p>
          <p v-if="$store.state.user.account.requiresTfa">
            {{ $t('Your administrator requires two-factor authentication for all accounts.') }}
          </p>
          <el-form-item>
            <el-button v-if="!isTfaEnabled" @click="enableTfa">
              {{ $t('Enable Two-factor Authentication') }}
            </el-button>
            <el-button
              v-else
              type="danger"
              :loading="isDisablingTfa"
              :disabled="$store.state.user.account.requiresTfa"
              @click="disableTfa"
            >
              {{ $t('Disable Two-factor Authentication') }}
            </el-button>
          </el-form-item>

          <!-- API access -->
          <div v-if="$store.state.user.isAdmin">
            <hr />

            <h2>{{ $t('API Access') }}</h2>
            <p>
              {{ $t('Enable access to programmatically manage sites, users, and more.') }}
              <require-plan plan="agency" />
            </p>

            <div v-if="$store.state.user.account.plan === 'agency'">
              <el-form-item>
                <el-button v-if="!apiToken" :loading="isApiUpdating" @click="enableApi()">
                  {{ $t('Enable API Access') }}
                </el-button>
                <el-button v-else type="danger" :loading="isApiUpdating" @click="disableApi()">
                  {{ $t('Disable API Access') }}
                </el-button>
              </el-form-item>

              <el-form-item v-if="apiToken" :label="$t('Personal Access Token')">
                <div style="display: flex">
                  <el-input
                    ref="apiToken"
                    :value="apiToken"
                    :type="showApiToken ? 'text' : 'password'"
                    prefix-icon="el-icon-fa-key"
                    :readonly="true"
                    :disabled="isApiUpdating"
                    style="flex: 1 1 auto; margin-right: 0.5rem"
                  >
                    <a slot="suffix" href="#" @click.prevent="showApiToken = !showApiToken">
                      <i
                        :class="{
                          fal: true,
                          'el-icon-fa-eye': !showApiToken,
                          'el-icon-fa-eye-slash': showApiToken
                        }"
                      />
                    </a>
                  </el-input>
                  <el-button style="flex: 0 0 auto" :disabled="isApiUpdating" @click="regenerateApiToken()">
                    {{ $t('Regenerate') }}
                  </el-button>
                </div>
                <div class="text-form-description">
                  {{
                    $t(
                      'This is your API token. Protect it like you would a password. If your token is ever compromised, you can regenerate it using the button above.'
                    )
                  }}
                </div>
              </el-form-item>
            </div>

            <p>
              <a href="https://www.surrealcms.com/docs/api.html" target="_blank">{{ $t('Visit Documentation') }}</a>
            </p>
          </div>
        </el-tab-pane>
      </el-tabs>
    </content-box>

    <div v-if="activeTab !== 'authentication'" class="text-right">
      <!-- Cancel -->
      <el-button v-if="!isProfile" @click="$emit('cancel')">
        {{ $t('Cancel') }}
      </el-button>

      <!-- Submit -->
      <el-button :loading="saving" type="primary" native-type="submit">
        <span v-if="isNew">{{ $t('Create User') }}</span>
        <span v-else>{{ $t('Save Changes') }}</span>
      </el-button>
    </div>
  </el-form>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.avatar-input {
  width: 128px;
  margin: 0 auto;

  /deep/ {
    .image-input-button {
      padding: 1px;
    }

    .image-input-button,
    .image-input-image {
      width: 128px;
      border-radius: 50%;
    }
  }
}

.admin-only-options {
  margin-left: 1rem;
}
</style>
